import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Typography } from '../typography/typography';
import classNames from 'classnames';
import { DataTableColumn, DataTableProps, DataTableRow } from './data-table';
import { Paginator } from '../paginator/paginator-v2';
import { PaginationRequest } from '../../clients/quipli-service/pagination/pagination';
import { Spinner } from '../spinner/spinner';
import { Checkbox } from '../checkbox/checkbox';
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { StaticTableRow } from './static-table-row';
import { DraggableRow } from './draggable-row';

export const SortableDesktopDataTable = <TPaginator extends PaginationRequest>({
  data,
  dataItems = [],
  setNewDataItemsOrder,
  columnNames,
  title,
  tabs,
  noDataText = 'No data available',
  isLoading,
  pagination,
  request,
  setRequest,
  cypressPrevious,
  cypressNext,
  wrapperClassNames = '',
  onCheckBoxSelect,
  checkBoxActions,
  ...props
}: DataTableProps<TPaginator>) => {
  const [selectedItems, setSelectedItems] = useState<Array<number>>([]);

  useEffect(() => {
    setSelectedItems([]);
  }, [pagination, data]);

  useEffect(() => {
    onCheckBoxSelect &&
      onCheckBoxSelect(
        (data.filter((elem, idx) => selectedItems.includes(idx)) || []).map((elem: DataTableRow) => elem?.data),
      );
  }, [selectedItems]);

  const sensors = useSensors(useSensor(MouseSensor, {}), useSensor(TouchSensor, {}), useSensor(KeyboardSensor, {}));
  const [activeId, setActiveId] = useState<number | undefined>();

  function handleDragEnd(event: { active: any; over: any }) {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = dataItems.findIndex((elem) => elem.id === active.id);
      const newIndex = dataItems.findIndex((elem) => elem.id === over.id);
      setNewDataItemsOrder(arrayMove(dataItems, oldIndex, newIndex));
    }
    setActiveId(undefined);
  }

  function handleDragStart(event: any) {
    setActiveId(event.active.id);
  }

  const selectedRow = (() => {
    if (activeId !== undefined && activeId >= 0) {
      const activeIndex = dataItems.findIndex((elem) => elem.id === activeId);
      return { row: data[activeIndex], idx: activeIndex };
    } else {
      return null;
    }
  })();

  return (
    <>
      {title && <h4 className={`mb-6 font-light capitalize text-gray-700 ${title.className}`}>{title.text}</h4>}

      <div className="relative w-full">
        {selectedItems.length > 0 && (
          <div className="table-checkbox-actions rounded-[12px]">
            <div className="flex pl-2">
              <Checkbox
                type="white"
                isChecked={true}
                onChange={() => setSelectedItems([])}
                icon={
                  <svg width="12" height="2" viewBox="0 0 12 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect width="12" height="2" rx="1" fill="#1D2939" />
                  </svg>
                }
              />
              <Typography
                type="text-md"
                className="text-[white]"
              >{`${selectedItems.length} items selected`}</Typography>
            </div>

            <div className="pr-2">{checkBoxActions && checkBoxActions}</div>
            <div className="table-checkbox-actions-vector">
              <svg width="20" height="8" viewBox="0 0 20 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M10 8C7 8 4.0001 -1.39876e-06 6.99382e-07 -1.74846e-06L20 0C16.0251 -3.47497e-07 13 8 10 8Z"
                  fill="#1D2939"
                />
              </svg>
            </div>
          </div>
        )}
      </div>

      <div
        className={classNames(
          'data-table overflow-x-scroll overflow-y-visible bg-white rounded-[12px]',
          wrapperClassNames,
        )}
      >
        {tabs && tabs}
        <DndContext
          sensors={sensors}
          onDragEnd={handleDragEnd}
          onDragStart={handleDragStart}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
        >
          <table {...props}>
            <thead>
              <DataTableHeader
                data={data}
                columnNames={columnNames}
                selectedItems={selectedItems}
                setSelectedItems={setSelectedItems}
              />
            </thead>
            <tbody>
              <SortableContext items={dataItems} strategy={verticalListSortingStrategy}>
                {data.length === 0 || (isLoading !== undefined && isLoading) ? (
                  <tr>
                    <td className="text-center">
                      {isLoading ? (
                        <Spinner />
                      ) : (
                        <Typography type="text-sm" className="text-grey-500">
                          {noDataText}
                        </Typography>
                      )}
                    </td>
                  </tr>
                ) : (
                  <SortTableDataTableBody
                    columnNames={columnNames}
                    data={data}
                    dataItems={dataItems}
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                  />
                )}
              </SortableContext>
            </tbody>
          </table>

          <DragOverlay wrapperElement="table">
            <thead className="collapse">
              <DataTableHeader
                data={data}
                columnNames={columnNames}
                selectedItems={selectedItems}
                setSelectedItems={setSelectedItems}
              />
            </thead>
            {activeId && (
              <tbody>
                <StaticTableRow
                  selectedItems={selectedItems}
                  setSelectedItems={setSelectedItems}
                  row={selectedRow && selectedRow.row}
                  idx={selectedRow?.idx}
                  columnNames={columnNames}
                />
              </tbody>
            )}
          </DragOverlay>
        </DndContext>
      </div>

      {setRequest && pagination && (
        <Paginator
          request={request}
          setRequest={(paginator) => setRequest((prev) => ({ ...prev, ...paginator }))}
          pagination={pagination}
          cypressPrevious={cypressPrevious}
          cypressNext={cypressNext}
        />
      )}
    </>
  );
};

const DataTableHeader = ({
  data,
  columnNames,
  selectedItems,
  setSelectedItems,
}: {
  data: DataTableRow[];
  columnNames: DataTableColumn[];
  selectedItems: Array<number>;
  setSelectedItems: Dispatch<SetStateAction<number[]>>;
}) => {
  return (
    <tr>
      {columnNames.map(
        ({ key, label, fixedAsFirst, fixedAsLast, isCheckBoxColumn, hide = false, showRule = 'Both', className }) => {
          return (
            !hide &&
            showRule !== 'OnlyMobile' && (
              <th
                key={key}
                className={classNames('text-left', `${key}-col`, className, {
                  'sticky-col-first': fixedAsFirst,
                  'sticky-col-last': fixedAsLast,
                })}
              >
                {isCheckBoxColumn ? (
                  !selectedItems.length && (
                    <Checkbox
                      type="white"
                      isChecked={selectedItems.length > 0 && selectedItems.length === data.length}
                      onChange={(isChecked) => {
                        if (isChecked) {
                          setSelectedItems(data.map((elem, idx) => idx));
                        } else {
                          setSelectedItems([]);
                        }
                      }}
                    />
                  )
                ) : (
                  <Typography type="text-sm" className="text-grey-500">
                    {label !== undefined ? label : key}
                  </Typography>
                )}
              </th>
            )
          );
        },
      )}
    </tr>
  );
};

const SortTableDataTableBody = ({
  data,
  dataItems,
  columnNames,
  selectedItems,
  setSelectedItems,
}: {
  data: DataTableRow[];
  dataItems: any[];
  columnNames: DataTableColumn[];
  selectedItems: Array<number>;
  setSelectedItems: Dispatch<SetStateAction<number[]>>;
}) => {
  return (
    <>
      {data.map(({ fields, className, redirectTo, extraRow }, i) => {
        return (
          <DraggableRow
            columnNames={columnNames}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
            id={dataItems[i].id}
            idx={i}
            fields={fields}
            className={className}
            redirectTo={redirectTo}
            key={i}
            extraRow={extraRow}
          />
        );
      })}
    </>
  );
};
